import * as CL from '@design-system/component-library';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t } from '../../../../../common/i18n';
import { getSubscriptionDetailForDeviceChangeFromSessionStorage } from '../../../../../common/utils/checkoutUtils';
import { COST_CENTER_REGEX } from '../../../../../common/utils/validationUtils';
import { PurposeOfUseSelector } from '../../../../PurposeOfUse/components/PurposeOfUseSelector';
import {
  ContactCreationType,
  convertOrderItemToPurposeOrContact,
  scrollToError,
} from '../../../utils/contactOrPurposeUtils';
import { CopyUserInfoButton } from './components/CopyUserInfoButton';
import { CopyUserInfoSuccessMessage } from './components/CopyUserInfoSuccessMessage';
import { CustomCostCenterAndEmployeeNumber } from './components/CustomCostCenterAndEmployeeNumber';
import type { Contact } from '../../../../../generated/api/contact';
import type { OrderItemPurposeOrContact, PlaceOrderItemUnit } from '../../../placeOrderTypes.js';
import type { CharacteristicsFlagsCoCoResult } from '../../../utils/characteristicsFlagsUtilsCoCo.js';
import type { ReactNode } from 'react';
import type { FieldErrors } from 'react-hook-form';

export interface ContactOrPurposeOfUseProps {
  isMandatory: boolean;
  isCostCenterRequired?: boolean;
  contacts?: Contact[];
  onSyncToParentForm: (data: OrderItemPurposeOrContact) => Promise<boolean>;
  handleCreateUser: (data: OrderItemPurposeOrContact) => Promise<boolean>;
  handleCopyOutput: (purposeOfUseOrContact: OrderItemPurposeOrContact) => void;
  orderItem: PlaceOrderItemUnit;
  setAccordionState: (value: boolean) => void;
  onContactChange?: (contactId: string | undefined) => void;
  showCopyButton?: boolean;
  characteristicFlags?: CharacteristicsFlagsCoCoResult;
  contentBeforePurposeOfUse?: ReactNode;
  contentBeforeCostCenter?: ReactNode;
}

export interface ContactOrPurposeHandle {
  getErrors: FieldErrors<OrderItemPurposeOrContact>;
  getRequired: () => boolean;
  getValues: () => OrderItemPurposeOrContact;
  isDirty: () => boolean;
  setValues: (data: OrderItemPurposeOrContact, isCopy?: boolean) => void;
  scrollToContactError: (contactId: string) => void;
  syncToParentForm: () => Promise<void>;
  validate: () => Promise<boolean>;
}

export const PlaceOrderContactOrPurpose = forwardRef<ContactOrPurposeHandle, ContactOrPurposeOfUseProps>(
  (
    {
      isMandatory,
      isCostCenterRequired = false,
      contacts,
      onSyncToParentForm,
      handleCreateUser,
      orderItem,
      handleCopyOutput,
      setAccordionState,
      onContactChange,
      showCopyButton = false,
      characteristicFlags,
      contentBeforePurposeOfUse,
      contentBeforeCostCenter,
    },
    ref
  ) => {
    const [firstErrorId, setFirstErrorId] = useState<string>();
    const [showCopySuccess, setShowCopySuccess] = useState<boolean>(false);
    const [copiedState, setCopiedState] = useState<boolean>();
    const [required, setRequired] = useState<boolean>(false);
    const [isCreatingUser, setIsCreatingUser] = useState<boolean>(false);
    const [showContactError, setShowContactError] = useState(false);

    const methods = useForm<OrderItemPurposeOrContact>({
      mode: 'all',
      shouldFocusError: `${orderItem.basketItemId}-${orderItem.basketItemIndex}` === firstErrorId,
    });
    const touchedFieldsLength = Object.keys(methods.formState.touchedFields).length > 0;

    const {
      showPurposeOfUse,
      showCostCenter,
      showReference,
      showUserContact = true,
    } = characteristicFlags?.fieldsToShow ?? {};

    const onChangeContact = (selectedContactId: string) => {
      if (!selectedContactId) {
        return;
      }

      setAccordionState(true);
      setRequired(true);
      setShowCopySuccess(false);
      setShowContactError(false);

      methods.reset(
        {
          ...methods.getValues(),
          contactId: selectedContactId,
        },
        { keepDefaultValues: true, keepDirtyValues: true }
      );

      // Notify parent about contact change
      onContactChange?.(selectedContactId);
    };

    const handleSetValues = (data: OrderItemPurposeOrContact, isCopy?: boolean) => {
      methods.reset(
        {
          basketItemId: orderItem.basketItemId,
          basketItemIndex: orderItem.basketItemIndex,
          contactId: data.contactId,
          costCenter: data.costCenter,
          email: data.email,
          employeeNumber: data.employeeNumber,
          firstName: data.firstName,
          lastName: data.lastName,
          purposeOfUse: data.purposeOfUse,
          phoneNumber: data.phoneNumber,
          selected: data.selected,
          additionalParameters: data.additionalParameters,
        },
        { keepDefaultValues: true }
      );

      if (isCopy) {
        setCopiedState(
          `${data.basketItemId}-${data.basketItemIndex}` !== `${orderItem.basketItemId}-${orderItem.basketItemIndex}`
        );
        setShowCopySuccess(true);
      }
    };

    useEffect(() => {
      const { basketItemId, basketItemIndex } = methods.getValues();
      const hasErrors = Object.keys(methods.formState.errors).length > 0;

      if (!hasErrors && !(basketItemId && basketItemIndex != null)) {
        const initialData = convertOrderItemToPurposeOrContact(orderItem);
        const subscriptionDetail = getSubscriptionDetailForDeviceChangeFromSessionStorage();
        if (!initialData.contactId && subscriptionDetail && contacts && contacts.length > 0) {
          initialData.contactId = contacts?.find(
            contact => contact.contactId === subscriptionDetail?.contactId
          )?.contactId;
        }

        methods.reset(initialData, {
          keepDefaultValues: true,
        });
      }
    }, [contacts, methods, orderItem]);

    useEffect(() => {
      const { contactId, purposeOfUse } = methods.getValues();

      // Required: mandatory when isMandatory prop is true, or when user has filled in data
      setRequired(!!contactId || !!purposeOfUse || copiedState || touchedFieldsLength || isMandatory);

      // Accordion state: open when there's data or when mandatory
      setAccordionState(!!contactId || !!purposeOfUse || copiedState || touchedFieldsLength || isMandatory);
    }, [isMandatory, copiedState, methods, setAccordionState, touchedFieldsLength]);

    const handleValidate = async (): Promise<boolean> => {
      // Block submission if user selected "Create new" but hasn't actually created the contact
      const currentContactId = methods.getValues().contactId;
      if (currentContactId === ContactCreationType.CREATE_NEW_CONTACT) {
        setShowContactError(true);
        return false;
      }

      let success = false;
      await methods.handleSubmit(
        async data => {
          success = await onSyncToParentForm({
            ...data,
            basketItemId: methods.getValues().basketItemId,
            basketItemIndex: methods.getValues().basketItemIndex,
          });
        },
        () => methods.formState.errors
      )();
      return success;
    };

    const handleCopyUserInfo = async () => {
      await methods.handleSubmit(data => {
        handleCopyOutput({
          ...data,
          basketItemId: methods.getValues().basketItemId,
          basketItemIndex: methods.getValues().basketItemIndex,
        });
      })();
    };

    const clearCopiedState = () => {
      setCopiedState(false);
      setShowCopySuccess(false);
    };

    /**
     * Handler for "Luo uusi käyttäjä" button click.
     * Validates the form fields and calls handleCreateUser to create the contact.
     * Shows validation errors in form fields if validation fails.
     */
    const onCreateUserClick = async () => {
      // Use handleSubmit to validate - this properly triggers validation and shows errors
      await methods.handleSubmit(
        // onValid - form is valid, create the user
        async data => {
          setIsCreatingUser(true);
          try {
            await handleCreateUser({
              ...data,
              basketItemId: orderItem.basketItemId,
              basketItemIndex: orderItem.basketItemIndex,
            });
          } finally {
            setIsCreatingUser(false);
          }
        }
        // onInvalid callback not needed - errors are automatically displayed by react-hook-form
      )();
    };

    /**
     * Writes the current field values to the parent form without validating them.
     * Needed for optional sections (e.g. purchased devices) that are never validated, so that the
     * user / purpose of use they contain still ends up in the product order request.
     */
    const handleSyncToParentForm = async (): Promise<void> => {
      const data = methods.getValues();
      if (!data.basketItemId || data.contactId === ContactCreationType.CREATE_NEW_CONTACT) {
        return;
      }
      await onSyncToParentForm(data);
    };

    useImperativeHandle(ref, () => ({
      getErrors: methods.formState.errors,
      getRequired: () => required,
      getValues: () => methods.getValues(),
      isDirty: () => methods.formState.isDirty,
      setValues: (data: OrderItemPurposeOrContact, isCopy?: boolean) => handleSetValues(data, isCopy),
      scrollToContactError: erroredContactId => {
        setFirstErrorId(erroredContactId);
        if (erroredContactId === `${orderItem.basketItemId}-${orderItem.basketItemIndex}`) {
          scrollToError(erroredContactId);
        }
      },
      syncToParentForm: () => handleSyncToParentForm(),
      validate: () => handleValidate(),
    }));

    // Watch contactId to check if user creation form is open
    const watchedContactId = methods.watch('contactId');
    const watchedCostCenter = methods.watch('costCenter');
    const isUserCreationInProgress = watchedContactId === ContactCreationType.CREATE_NEW_CONTACT;
    const hasContactCreationError = showContactError && isUserCreationInProgress;

    // Validate cost center for copy button - must be valid 4-digit number if required
    const isCostCenterValid = !isCostCenterRequired || (watchedCostCenter && COST_CENTER_REGEX.test(watchedCostCenter));

    // Hide copy button during user creation and when no user information is collected
    const copyButtonVisible = showCopyButton && showUserContact && !isUserCreationInProgress;
    const copySuccessMessageVisible = showCopySuccess && (showCopyButton || copiedState);

    return (
      <div
        className={`of-place-order-contact-or-purpose ${hasContactCreationError ? 'of-place-order__step-content--error' : ''}`}
        id={`${orderItem.basketItemId}-${orderItem.basketItemIndex}`}
      >
        <FormProvider {...methods}>
          {(showUserContact || showPurposeOfUse !== false) && (
            <PurposeOfUseSelector
              contacts={contacts}
              onChangeContact={onChangeContact}
              onContactOrPurposeChange={() => setShowCopySuccess(false)}
              onCreateUser={onCreateUserClick}
              isCreatingUser={isCreatingUser}
              showPurposeOfUse={showPurposeOfUse}
              showUserContact={showUserContact}
              contentAfterHeader={contentBeforePurposeOfUse}
              isUserOrPurposeMandatory={isMandatory}
            />
          )}
          {contentBeforeCostCenter}
          <CustomCostCenterAndEmployeeNumber
            isCostCenterRequired={isCostCenterRequired}
            showCostCenter={showCostCenter}
            showReference={showReference}
          />
          {copySuccessMessageVisible ? (
            <CopyUserInfoSuccessMessage isCopiedUserInfo={copiedState} onClose={() => clearCopiedState()} />
          ) : (
            copyButtonVisible && <CopyUserInfoButton onClick={handleCopyUserInfo} disabled={!isCostCenterValid} />
          )}
        </FormProvider>
        {hasContactCreationError && (
          <CL.InputError>{t.RJT5('Select a contact from the list or create a new one.')}</CL.InputError>
        )}
      </div>
    );
  }
);
